import { useEffect, useMemo, useRef, useState } from 'react';
import { callUIAPI, searchEntityRecords } from '@utils/apiUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, ChevronRight, Flag, LayoutGrid, MoreHorizontal } from 'lucide-react';

import { APP_ROUTES } from '@/app/routePaths';
import { _t } from '@/i18n/t';
import {
  ENTITY_NAME_CAMPAIGN,
  ENTITY_NAME_COMPANY,
  ENTITY_NAME_CONTENT,
  ENTITY_NAME_PERSONA,
  ENTITY_NAME_WRITING_STYLE,
} from '@/shared/adapters/constants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';

type OnboardingCounts = {
  companies: number;
  campaigns: number;
  personas: number;
  writingStyles: number;
  content: number;
};

type OnboardingItem = {
  key: string;
  label: string;
  path: string;
  helper?: string;
  completed: boolean;
  count: number;
  optional?: boolean;
  relatedPaths?: string[];
  subItems?: Array<{
    key: string;
    label: string;
    path: string;
    count: number;
    completed: boolean;
    relatedPaths?: string[];
  }>;
};

const DEFAULT_COUNTS: OnboardingCounts = {
  companies: 0,
  campaigns: 0,
  personas: 0,
  writingStyles: 0,
  content: 0,
};

const PANEL_CLASS = 'shrink-0 bg-[linear-gradient(180deg,hsl(214_100%_95%)_0%,hsl(213_100%_97%)_100%)]';

const getCreatedLabel = (count: number) => (
  count === 1
    ? _t('onboarding.count.single', { count })
    : _t('onboarding.count.multiple', { count })
);

const StatusDot = ({ completed, active, size = 12 }: { completed: boolean; active: boolean; size?: number }) => (
  <span
    className="inline-flex items-center justify-center rounded-full"
    style={{
      width: size,
      height: size,
      border: completed || active ? 'none' : '2px solid hsl(var(--border))',
      backgroundColor: completed
        ? 'hsl(var(--foreground))'
        : active
          ? 'hsl(var(--primary))'
          : 'transparent',
    }}
  >
    {completed && <Check size={size - 4} strokeWidth={3} className="text-primary-foreground" />}
  </span>
);

const OnboardingGuide = ({ onHide }: { onHide: () => void }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('onboardingGuideCollapsed') === 'true');
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState<OnboardingCounts>(DEFAULT_COUNTS);
  const [refreshKey, setRefreshKey] = useState(0);
  const initialLoadDone = useRef(false);

  useEffect(() => {
    localStorage.setItem('onboardingGuideCollapsed', String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    const handleEntitySaved = () => setRefreshKey((current) => current + 1);
    window.addEventListener('entity-saved', handleEntitySaved);
    return () => window.removeEventListener('entity-saved', handleEntitySaved);
  }, []);

  useEffect(() => {
    const fetchOnboardingData = async () => {
      try {
        if (!initialLoadDone.current) {
          setLoading(true);
        }

        const searchParams = {
          pageNumber: 1,
          pageSize: 1,
          sortBy: 'name',
          sortDirection: 'asc',
        };

        const [
          companiesRes,
          campaignsRes,
          personasRes,
          writingStylesRes,
          contentRes,
        ] = await Promise.all([
          searchEntityRecords(ENTITY_NAME_COMPANY, searchParams),
          searchEntityRecords(ENTITY_NAME_CAMPAIGN, searchParams),
          searchEntityRecords(ENTITY_NAME_PERSONA, searchParams),
          searchEntityRecords(ENTITY_NAME_WRITING_STYLE, searchParams),
          searchEntityRecords(ENTITY_NAME_CONTENT, searchParams),
        ]);

        setCounts({
          companies: companiesRes?.data?.totalCount ?? 0,
          campaigns: campaignsRes?.data?.totalCount ?? 0,
          personas: personasRes?.data?.totalCount ?? 0,
          writingStyles: writingStylesRes?.data?.totalCount ?? 0,
          content: contentRes?.data?.totalCount ?? 0,
        });
      } catch {
        setCounts(DEFAULT_COUNTS);
      } finally {
        setLoading(false);
        initialLoadDone.current = true;
      }
    };

    void fetchOnboardingData();
  }, [location.pathname, refreshKey]);

  const items = useMemo<OnboardingItem[]>(() => ([
    {
      key: 'company',
      label: _t('sidebar.item.company'),
      path: APP_ROUTES.COMPANY_NEW,
      relatedPaths: [APP_ROUTES.COMPANIES],
      helper: _t('onboarding.helper.company'),
      completed: counts.companies > 0,
      count: counts.companies,
    },
    {
      key: 'brand-strategy',
      label: _t('onboarding.step.brandStrategy'),
      path: APP_ROUTES.WRITING_STYLES,
      relatedPaths: [APP_ROUTES.WRITING_STYLES, APP_ROUTES.PERSONAS],
      helper: _t('onboarding.helper.brandStrategy'),
      completed: counts.writingStyles > 0 || counts.personas > 0,
      count: counts.writingStyles + counts.personas,
      optional: true,
      subItems: [
        {
          key: 'writing-style',
          label: _t('sidebar.item.writingStyle'),
          path: APP_ROUTES.WRITING_STYLES,
          relatedPaths: [APP_ROUTES.WRITING_STYLES],
          completed: counts.writingStyles > 0,
          count: counts.writingStyles,
        },
        {
          key: 'persona',
          label: _t('sidebar.item.persona'),
          path: APP_ROUTES.PERSONAS,
          relatedPaths: [APP_ROUTES.PERSONAS],
          completed: counts.personas > 0,
          count: counts.personas,
        },
      ],
    },
    {
      key: 'campaign',
      label: _t('sidebar.item.campaign'),
      path: APP_ROUTES.CAMPAIGN_NEW,
      relatedPaths: [APP_ROUTES.CAMPAIGNS],
      helper: _t('onboarding.helper.campaign'),
      completed: counts.campaigns > 0,
      count: counts.campaigns,
    },
    {
      key: 'content',
      label: _t('dashboard.metric.content'),
      path: APP_ROUTES.CONTENTS,
      relatedPaths: [APP_ROUTES.CONTENTS],
      helper: _t('onboarding.helper.content'),
      completed: counts.content > 0,
      count: counts.content,
    },
  ] as OnboardingItem[]), [counts]);

  const completedCount = items.filter((item) => item.completed).length;
  const totalCount = items.length;
  const isAllComplete = completedCount === totalCount;

  const isStepActive = (item: Pick<OnboardingItem, 'path' | 'relatedPaths' | 'subItems'>) => {
    const paths = [item.path, ...(item.relatedPaths ?? [])];
    if (paths.some((path) => location.pathname.startsWith(path))) return true;

    return item.subItems?.some((subItem) => {
      const subPaths = [subItem.path, ...(subItem.relatedPaths ?? [])];
      return subPaths.some((path) => location.pathname.startsWith(path));
    }) ?? false;
  };

  const hidePanel = async () => {
    await callUIAPI('firms/show-onboarding', { showOnboarding: false }, 'put', true);
    onHide();
  };

  if (isAllComplete && collapsed) {
    return (
      <aside className={`${PANEL_CLASS} flex w-14 flex-col items-center pt-6`}>
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg text-foreground transition-colors hover:bg-muted"
          aria-label={_t('onboarding.action.systemPanel')}
        >
          <LayoutGrid size={18} />
        </button>
      </aside>
    );
  }

  if (isAllComplete && !collapsed) {
    return (
      <aside className={`${PANEL_CLASS} flex w-[280px] flex-col`}>
        <div className="flex items-start justify-between p-6">
          <div className="flex items-start gap-3">
            <LayoutGrid size={18} className="mt-1 text-foreground" />
            <div>
              <h3 className="text-sm font-semibold text-foreground">{_t('onboarding.title.ready')}</h3>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">{_t('onboarding.subtitle.ready')}</p>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={_t('onboarding.action.menu')}
              >
                <MoreHorizontal size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44 rounded-md bg-card p-1">
              <DropdownMenuItem className="cursor-pointer" onClick={() => void hidePanel()}>
                {_t('onboarding.action.hide')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="space-y-2 px-6 pb-6">
          {items.map((item) => (
            <div key={item.key} className="flex items-center gap-3 rounded-lg border border-solid border-border/40 bg-background/50 px-3 py-2">
              <Check size={14} strokeWidth={2.5} className="shrink-0 text-foreground" />
              <span className="text-sm text-foreground">{item.label}</span>
            </div>
          ))}
        </div>
      </aside>
    );
  }

  if (collapsed) {
    return (
      <aside className={`${PANEL_CLASS} flex w-14 flex-col items-center py-6`}>
        <Flag size={18} className="mb-4 text-foreground" />
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="relative mb-6 inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={_t('onboarding.action.expand')}
        >
          <ChevronLeft size={14} />
        </button>

        {!loading && (
          <div className="flex flex-1 flex-col items-center">
            <div className="mb-4 text-xs font-semibold text-muted-foreground">
              {completedCount} / {totalCount}
            </div>
            <div className="relative flex flex-col items-center gap-4">
              <div className="absolute bottom-2 top-2 w-px bg-border" />
              {items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => navigate(item.path)}
                  className="group relative z-10 inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors hover:bg-muted"
                  aria-label={item.label}
                >
                  <StatusDot completed={item.completed} active={isStepActive(item)} size={12} />
                  <span className="pointer-events-none absolute right-[calc(100%+8px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md border border-solid border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground opacity-0 shadow-sm transition-all group-hover:opacity-100">
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </aside>
    );
  }

  if (loading) {
    return (
      <aside className={`${PANEL_CLASS} flex w-[280px] flex-col`}>
        <div className="p-6">
          <h3 className="text-sm font-semibold text-foreground">{_t('onboarding.title.getStarted')}</h3>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{_t('onboarding.subtitle.getStarted')}</p>
        </div>
      </aside>
    );
  }

  return (
    <aside className={`${PANEL_CLASS} flex w-[280px] flex-col`}>
      <div className="flex items-start justify-between p-6">
        <div className="flex items-start gap-3">
          <Flag size={18} className="mt-1 text-foreground" />
          <div>
            <h3 className="text-sm font-semibold text-foreground">{_t('onboarding.title.getStarted')}</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">{_t('onboarding.subtitle.getStarted')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label={_t('onboarding.action.collapse')}
        >
          <ChevronRight size={14} />
        </button>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto px-3 pb-4">
        {items.map((item) => (
          <div key={item.key} className="space-y-2">
            <button
              type="button"
              onClick={() => navigate(item.path)}
              className={`flex w-full items-start gap-3 rounded-xl border border-solid px-3 py-3 text-left transition-colors ${
                isStepActive(item)
                  ? 'border-primary/30 bg-primary/5'
                  : item.completed
                    ? 'border-border/50 bg-background/70'
                    : 'border-transparent hover:bg-background/60'
              }`}
            >
              <div className="pt-1">
                <StatusDot completed={item.completed} active={isStepActive(item)} size={12} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">{item.label}</span>
                  {item.optional && (
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                      {_t('onboarding.badge.optional')}
                    </span>
                  )}
                </div>
                {!item.completed && item.helper && (
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">{item.helper}</p>
                )}
                {item.count > 0 && (
                  <p className="mt-1 text-xs font-medium text-foreground/80">{getCreatedLabel(item.count)}</p>
                )}
              </div>
            </button>

            {item.subItems && (
              <div className="ml-8 space-y-1.5">
                {item.subItems.map((subItem) => {
                  const subActive = [subItem.path, ...(subItem.relatedPaths ?? [])]
                    .some((path) => location.pathname.startsWith(path));

                  return (
                    <button
                      key={subItem.key}
                      type="button"
                      onClick={() => navigate(subItem.path)}
                      className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${
                        subActive ? 'bg-background/80' : 'hover:bg-background/60'
                      }`}
                    >
                      <StatusDot completed={subItem.completed} active={subActive} size={10} />
                      <span className="flex-1 text-xs font-medium text-foreground/90">{subItem.label}</span>
                      {subItem.count > 0 && (
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">{subItem.count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </aside>
  );
};

export default OnboardingGuide;